import {
  FunctionComponent,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import {
  Box,
  IconButton,
  Typography,
  Grid,
  SelectChangeEvent,
  Link,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { isEmpty, get, map, size } from 'lodash';
import classNames from 'classnames';
import ClearIcon from '@mui/icons-material/Clear';
import { useDispatch, useSelector } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import { toast } from 'react-toastify';

import Loader from 'elements/Loader';
import TextField from 'elements/Form/TextField';
import DropdownField from 'elements/DropdownField';
import FormControlLabel from 'elements/FormControlLabel/FormControlLabel';

import { getUserName, getQueryParam } from 'utils/utility';
import { useAppContext } from 'contexts/AppContext';
import { ReactComponent as CompletionIcon } from 'assets/images/completion-icon.svg';

import {
  getAdditionalLearning,
  getAdditionalLearningMetadata,
  updateFilters,
} from 'pages/BcmDashboard/components/AdditionalLearning/additionalLearningSlice';
import PathProgressModal from 'pages/BcmDashboard/components/PathInProgress/PathProgressModal';
import EmptyStateMessage from 'pages/BcmDashboard/components/MemberOverview/EmptyStateMessage';
import {
  Container,
  StickyDataGrid,
  SearchHolder,
} from 'pages/BcmDashboard/components/style';
import {
  SortedDescendingIcon,
  SortedAscendingIcon,
  UnsortedIcon,
} from 'pages/BcmDashboard/components/MemberOverview/index';
import { StyledSearchIcon } from 'components/Header/style';
import { scrollCellIntoView } from 'utils/utility';
import GridSkeleton from '../GridSkeleton';

const renderSectionCell = ({
  completed = 0,
  total = 0,
  approved = false,
} = {}) => {
  return approved ? <CompletionIcon /> : `${completed} of ${total}`;
};

const AdditionalLearning: FunctionComponent<any> = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<any>({});
  const [selectedClub, setSelectedClub] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState<any>([{}]);

  const dispatch = useDispatch();
  const { roles: userRoles = [] } = useAppContext();

  const {
    metadata = {},
    data = {},
    isLoadingMetadata,
    isLoadingData,
    isError,
    isFilterUpdated,
  } = useSelector((state: any) => state.additionalLearning);

  const isLoading = isLoadingMetadata || isLoadingData;

  const getNextData = useCallback(() => {
    const next = get(data, 'next', null);
    const { page }: any = getQueryParam(next || '');

    if (!next) {
      return;
    }

    dispatch(
      getAdditionalLearning({ club: selectedClub, search: searchFilter, page }),
    );
  }, [data, dispatch, selectedClub, searchFilter]);

  const clubOptions = useMemo(
    () =>
      userRoles.reduce((acc: any, value: any) => {
        const { name, uuid, roles = [] } = value;

        const hasBcmRole = roles.some(({ isBcm }: any) => isBcm);

        if (!hasBcmRole) {
          return acc;
        }

        return [...acc, { label: name, value: uuid }];
      }, []),
    [userRoles],
  );

  const maxSections = useMemo(() => {
    return get(metadata, 'maxSections', 0);
  }, [metadata]);


  const rows = useMemo(() => {
  
  const builtRows = map(get(data, 'results', []), (value: any, index) => {
    const { user, courseName, courseId, progression = {} } = value;

    const rowData: any = {
      id: index,
      name: getUserName(user),
      user,
      courseName: courseName,
      courseId: courseId,
    };

    Object.keys(progression).forEach((key) => {
      
      if (key.toLowerCase().startsWith('section')) {
        const sectionNum = key.replace(/section/i, '');
        const fieldName = `section_${sectionNum}`;
        rowData[fieldName] = progression[key];
      } else if (key === 'learningCompletion') {
        rowData.learningCompletion = progression[key];
      }
    });

    return rowData;
  });
  
  return builtRows;
}, [data]);

  const onSortModelChange = useCallback(
    (newSortModel: any) => {
      const { field, sort } = sortModel[0] || {};
      if (isEmpty(newSortModel) && sort === 'desc') {
        setSortModel([{ field, sort: 'asc' }]);
        return;
      }

      setSortModel(newSortModel);
    },
    [sortModel],
  );

  useEffect(() => {
    if (clubOptions.length === 1) {
      setSelectedClub(clubOptions[0].value);
    }
  }, [clubOptions]);

  useEffect(() => {
    if (!selectedClub) {
      return;
    }

    dispatch(getAdditionalLearningMetadata({ club: selectedClub }));
  }, [dispatch, selectedClub]);

  // Fetch data after metadata is loaded AND when searchFilter changes
  useEffect(() => {
    if (!selectedClub) {
      return;
    }

    if (isLoadingMetadata) {
      return;
    }

    if (isEmpty(metadata)) {
      return;
    }

    dispatch(updateFilters());
    dispatch(
      getAdditionalLearning({
        club: selectedClub,
        search: searchFilter,
        resetInitialState: true,
      }),
    );
  }, [dispatch, selectedClub, searchFilter, isLoadingMetadata, metadata]);

  const handleClubChange = (event: SelectChangeEvent) => {
    setSelectedClub(event.target.value);
    setSearchText('');
    setSearchFilter('');
  };

  const handleUpdateTextSearch = (event: any) => {
    const { value } = event.target;
    setSearchText(value);
  };

  const handleSearch = () => {
    if (isLoading) {
      return;
    }

    if (!searchText || searchText === searchFilter) {
      return;
    }

    setSearchFilter(searchText);
  };

  const handleClearSearch = () => {
    if (isLoading) {
      return;
    }

    setSearchText('');
    setSearchFilter('');
  };

  const handleCloseProgressModal = () => {
    setIsModalOpen(false);
    setSelectedCourse({});
  };

  const handleOpenProgressModal = (selectedRow: any) => {
    setIsModalOpen(true);
    setSelectedCourse(selectedRow);
  };

  const { user, courseName, name, courseId } = selectedCourse;

  // Build dynamic columns based on metadata
  const columns: GridColDef[] = useMemo(() => {
  
  const baseColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 138,
      renderCell: (params: any) => {
        return (
          <Link
            href='#'
            variant='body1'
            color='secondary'
            sx={{
              fontSize: '0.9rem',
            }}
            tabIndex={params.hasFocus ? 0 : -1}
            onClick={() => handleOpenProgressModal(params.row)}
          >
            {params.row.name}
          </Link>
        );
      },
      renderHeader: () => (
        <Typography variant='h4' component='h3'>
          Name
        </Typography>
      ),
    },
    {
      field: 'courseName',
      headerName: 'Learning',
      width: 200,
      renderHeader: () => (
        <Typography variant='h4' component='h3'>
          Learning
        </Typography>
      ),
      renderCell: (params: any) => {
        return params.value || '--';
      },
    },
  ];

  for (let i = 1; i <= maxSections; i++) {
    baseColumns.push({
      field: `section_${i}`,
      headerName: `Section ${i}`,
      align: 'center',
      width: 138,
      sortable: false,
      renderHeader: () => (
        <Typography variant='h4' component='h3'>
          Section {i}
        </Typography>
      ),
      renderCell: (params: any) => {
        const sectionData = params.row[`section_${i}`];
        if (!sectionData) {
          return '--';
        }
        return renderSectionCell(sectionData);
      },
    });
  }

  baseColumns.push({
    field: 'learningCompletion',
    headerName: 'Learning Completion',
    width: 170,
    align: 'center',
    sortable: false,
    renderHeader: () => (
      <Typography variant='h4' component='h3'>
        Learning Completion
      </Typography>
    ),
    renderCell: (params: any) => {
      const completionData = params.row.learningCompletion;
      if (!completionData) {
        return '--';
      }
      const { completed = 0, total = 0 } = completionData;
      return `${completed} of ${total}`;
    },
  });

  return baseColumns;
}, [maxSections]);

  return (
    <Box mb={10}>
      <Typography variant='h2'>Additional Learning</Typography>
      <Typography>
        The table below tracks members' progress in additional learning courses
        by indicating how many projects have been completed for each section. A
        green checkmark will appear when a section has been submitted by the
        member and approved by a Base Camp manager. Once a Learning Completion has been submitted and
        approved, it will move to the Member Overview section. Click on the name of a member
        to see additional project details.
      </Typography>
      <Grid
        container
        mt={2}
        gap={3}
        alignItems='flex-end'
        justifyContent='space-between'
      >
        {size(clubOptions) > 1 && (
          <Grid item xs={12} sm={5}>
            <FormControlLabel
              label='Club'
              labelPlacement='top'
              id='club'
              control={
                <DropdownField
                  value={selectedClub}
                  options={clubOptions}
                  handleChange={handleClubChange}
                  placeholder='Select a Club'
                  disabled={isLoading}
                  inputProps={{ 'aria-labelledby': 'club' }}
                />
              }
            />
            {!selectedClub && (
              <Typography variant='body2' mt={4}>
                Please select a club from the dropdown menu above.
              </Typography>
            )}
          </Grid>
        )}
      </Grid>
      <Grid
        container
        gap={3}
        alignItems='flex-end'
        justifyContent='space-between'
      >
        {selectedClub && (
          <Grid item xs={12} sm={5} mt={5}>
            <SearchHolder>
              <TextField
                inputProps={{
                  'aria-label': 'Find record',
                }}
                placeholder='Search by Keyword'
                fullWidth
                value={searchText}
                onChange={handleUpdateTextSearch}
                onKeyPress={(event: any) => {
                  if (event.key === 'Enter') handleSearch();
                }}
                className={searchText ? 'has-data' : ''}
                disabled={isLoading}
              />
              {searchText && (
                <IconButton
                  onClick={() => handleClearSearch()}
                  className={classNames('clear-btn', { disable: isLoading })}
                  aria-label='Clear'
                >
                  <ClearIcon />
                </IconButton>
              )}
              <StyledSearchIcon
                aria-label='Search'
                onClick={(): void => {
                  handleSearch();
                }}
                className={classNames({ disable: isLoading || !searchText })}
              />
            </SearchHolder>
          </Grid>
        )}
        <Grid item xs={12} sm={5} className='text-end'>
          {/* Export button placeholder */}
        </Grid>
      </Grid>
      <Container>
        {isFilterUpdated && isLoading ? (
          <GridSkeleton rows={4} columns={maxSections + 3} />
        ) : !isEmpty(data?.results) && selectedClub && !isError ? (
          <InfiniteScroll
            dataLength={get(data, 'results', []).length}
            next={getNextData}
            hasMore={get(data, 'next', '') ? true : false}
            loader={
              <div className='loader' key={0}>
                <GridSkeleton columns={maxSections + 3} />
              </div>
            }
            scrollableTarget='scrollableDiv'
            style={{ overflow: 'unset' }}
          >
            <StickyDataGrid
              autoHeight
              getRowHeight={() => 'auto'}
              columns={columns}
              disableRowSelectionOnClick
              disableColumnFilter
              disableColumnMenu
              slots={{
                columnSortedDescendingIcon: SortedDescendingIcon,
                columnSortedAscendingIcon: SortedAscendingIcon,
                columnUnsortedIcon: UnsortedIcon,
              }}
              rows={rows}
              className={classNames('progress', {
                'disable-pagination': isLoading,
              })}
              aria-label='Additional Learning Table'
              sortModel={sortModel}
              onSortModelChange={onSortModelChange}
              hideFooter
              slotProps={{
                cell: {
                  tabindex: '0',
                  onFocus: (event: any) =>
                    scrollCellIntoView(event.currentTarget),
                },
              }}
            />
          </InfiniteScroll>
        ) : (
          <EmptyStateMessage
            isSuccess={!isEmpty(data)}
            data={data?.results}
            isError={isError}
          />
        )}
      </Container>
      {isModalOpen && (
        <PathProgressModal
          username={user?.username}
          handleClose={handleCloseProgressModal}
          title={`${courseName} Progress for ${name}`}
          className='modal-xl'
          courseId={courseId}
          labelType='learning' // Pass prop to change "Path" to "Learning"
        />
      )}
    </Box>
  );
};

export default AdditionalLearning;
